// src/pages/ProfilePage.tsx - TEAL/INK/GOLD THEME + MOBILE-FIRST LAYOUT
// ALL LOGIC, STATE, HANDLERS, SUPABASE CALLS UNTOUCHED
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import { Post } from '../services/postService';
import {
  followUser,
  isFollowing,
  getFollowers,
  getFollowing,
  FollowUser
} from '../services/followService';
import { getSavedPosts, unsavePost, SavedPost } from '../services/savedPostsService';
import toast from 'react-hot-toast';
import {
  FaUserCircle, FaMapMarkerAlt, FaLink, FaEdit, FaTimes,
  FaHeart, FaComment, FaCalendar, FaCrown, FaVideo,
  FaThLarge, FaList, FaSpinner, FaStar, FaCamera,// eslint-disable-next-line
  FaImage, FaBolt, FaFire, FaGamepad, FaPalette, // eslint-disable-next-line
  FaMusic, FaBook, FaCode, FaPlus, FaCheck, // eslint-disable-next-line
  FaUserPlus, FaUserCheck, FaUsers, FaBookmark, FaTrash
} from 'react-icons/fa';

// ── TYPES (unchanged) ────────────────────────────────────────────
interface UserProfile {
  id: string; username: string; email: string;
  avatar_url: string | null; banner_url: string | null;
  bio: string | null; full_name: string | null;
  location: string | null; website: string | null;
  favorite_anime: string[] | null; interests: string[] | null;
  level: number; ispremium: boolean;
  followers_count: number; following_count: number;
  created_at: string;
}

interface ProfilePageProps { darkMode?: boolean; userId?: string; }

// ── CONSTANTS (unchanged) ────────────────────────────────────────
const AVAILABLE_INTERESTS = [
  { id: 'anime',     label: 'Anime',      icon: FaFire,    color: '#e8a820' },
  { id: 'manga',     label: 'Manga',      icon: FaBook,    color: '#a855f7' },
  { id: 'gaming',    label: 'Gaming',     icon: FaGamepad, color: '#3b82f6' },
  { id: 'art',       label: 'Digital Art',icon: FaPalette, color: '#10b981' },
  { id: 'music',     label: 'J-Pop/K-Pop',icon: FaMusic,   color: '#ec4899' },
  { id: 'cosplay',   label: 'Cosplay',    icon: FaStar,    color: '#f59e0b' },
  { id: 'coding',    label: 'Coding',     icon: FaCode,    color: '#6366f1' },
  { id: 'streaming', label: 'Streaming',  icon: FaVideo,   color: '#8b5cf6' },
];

const LEVEL_RANKS = [
  { min: 1,   max: 9,   title: 'Rookie',      color: '#888780' },
  { min: 10,  max: 24,  title: 'Rising Star',  color: '#5ab8b8' },
  { min: 25,  max: 49,  title: 'Elite',        color: '#a855f7' },
  { min: 50,  max: 99,  title: 'Master',       color: '#e8a820' },
  { min: 100, max: 999, title: 'Legend',       color: '#e05050' },
];

// ── COMPONENT ────────────────────────────────────────────────────
export const ProfilePage: React.FC<ProfilePageProps> = ({ darkMode = true, userId }) => {
  const { user: currentUser, refreshUser } = useAuth();

  // ── ALL STATE UNTOUCHED ──────────────────────────────────────
  const [profile, setProfile]           = useState<UserProfile | null>(null);
  const [userPosts, setUserPosts]       = useState<Post[]>([]);
  const [loading, setLoading]           = useState(true);
  const [activeTab, setActiveTab]       = useState<'posts' | 'media' | 'saved'>('posts');
  const [viewMode, setViewMode]         = useState<'grid' | 'list'>('grid');
  const [savedPosts, setSavedPosts]     = useState<SavedPost[]>([]);
  const [loadingSavedPosts, setLoadingSavedPosts] = useState(false);
  const [removingId, setRemovingId]     = useState<string | null>(null);
  const [isFollowingUser, setIsFollowingUser]   = useState(false);
  const [followLoading, setFollowLoading]       = useState(false);
  const [followersData, setFollowersData]       = useState<FollowUser[]>([]);
  const [followingData, setFollowingData]       = useState<FollowUser[]>([]);
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [showFollowingModal, setShowFollowingModal] = useState(false);
  const [loadingFollowers, setLoadingFollowers] = useState(false);
  const [loadingFollowing, setLoadingFollowing] = useState(false);
  const [isEditing, setIsEditing]               = useState(false);
  const [editedProfile, setEditedProfile]       = useState<Partial<UserProfile>>({});
  const [isUpdating, setIsUpdating]             = useState(false);
  const [editingInterests, setEditingInterests] = useState(false);
  const [tempInterests, setTempInterests]       = useState<string[]>([]);
  const [newAnime, setNewAnime]                 = useState('');
  const [uploadingAvatar, setUploadingAvatar]   = useState(false);
  const [uploadingBanner, setUploadingBanner]   = useState(false);

  const profileUserId = userId || currentUser?.id;
  const isOwnProfile  = currentUser?.id === profileUserId;

  // ── ALL HANDLERS UNTOUCHED ───────────────────────────────────
  useEffect(() => {
    if (profileUserId) {
      loadProfile();
      loadUserPosts();
      if (!isOwnProfile) checkFollowStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileUserId]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.from('users').select('*').eq('id', profileUserId).single();
      if (error) throw error;
      setProfile(data);
      setEditedProfile(data);
      setTempInterests(data.interests || []);
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadUserPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*, author:users(id,username,avatar_url,level,ispremium)')
        .eq('user_id', profileUserId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setUserPosts(data || []);
    } catch (error) { console.error('Failed to load posts:', error); }
  };

  const loadSavedPosts = async () => {
    if (!profileUserId || !isOwnProfile) return;
    try {
      setLoadingSavedPosts(true);
      const posts = await getSavedPosts(profileUserId);
      setSavedPosts(posts);
    } catch (error) {
      console.error('Error loading saved posts:', error);
      toast.error('Failed to load saved posts');
    } finally { setLoadingSavedPosts(false); }
  };

  const handleUnsave = async (postId: string) => {
    if (!profileUserId) return;
    setRemovingId(postId);
    try {
      const success = await unsavePost(profileUserId, postId);
      if (success) { setSavedPosts(prev => prev.filter(sp => sp.post_id !== postId)); toast.success('Removed from saved posts'); }
      else toast.error('Failed to remove post');
    } catch (error) { console.error('Error unsaving post:', error); toast.error('Failed to remove post'); }
    finally { setRemovingId(null); }
  };

  const checkFollowStatus = async () => {
    if (!profileUserId) return;
    const following = await isFollowing(profileUserId);
    setIsFollowingUser(following);
  };

  const handleFollowToggle = async () => {
    if (!profileUserId || !currentUser) return;
    try {
      setFollowLoading(true);
      const nowFollowing = await followUser(profileUserId);
      setIsFollowingUser(nowFollowing);
      setProfile(prev => prev ? { ...prev, followers_count: prev.followers_count + (nowFollowing ? 1 : -1) } : null);
      toast.success(nowFollowing ? 'Following! ✨' : 'Unfollowed 👋');
    } catch (error) { console.error('Follow error:', error); toast.error('Failed to update follow status'); }
    finally { setFollowLoading(false); }
  };

  const handleLoadFollowers = async () => {
    if (!profileUserId) return;
    try {
      setLoadingFollowers(true);
      const data = await getFollowers(profileUserId);
      setFollowersData(data);
      setShowFollowersModal(true);
    } catch (error) { console.error('Error loading followers:', error); toast.error('Failed to load followers'); }
    finally { setLoadingFollowers(false); }
  };

  const handleLoadFollowing = async () => {
    if (!profileUserId) return;
    try {
      setLoadingFollowing(true);
      const data = await getFollowing(profileUserId);
      setFollowingData(data);
      setShowFollowingModal(true);
    } catch (error) { console.error('Error loading following:', error); toast.error('Failed to load following'); }
    finally { setLoadingFollowing(false); }
  };

  const handleUpdateProfile = async () => {
    try {
      setIsUpdating(true);
      const { error } = await supabase.from('users').update({
        username: editedProfile.username, full_name: editedProfile.full_name,
        bio: editedProfile.bio, location: editedProfile.location,
        website: editedProfile.website, favorite_anime: editedProfile.favorite_anime,
        interests: editedProfile.interests, updated_at: new Date().toISOString()
      }).eq('id', currentUser!.id);
      if (error) throw error;
      setProfile(prev => ({ ...prev!, ...editedProfile }));
      setIsEditing(false);
      await refreshUser();
      toast.success('Profile updated! ✨', { icon: '🎨' });
    } catch (error: any) {
      console.error('Update error:', error);
      toast.error(error.message || 'Failed to update profile');
    } finally { setIsUpdating(false); }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be less than 5MB'); return; }
    try {
      setUploadingAvatar(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${currentUser!.id}-avatar-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;
      const { error: uploadError } = await supabase.storage.from('media').upload(filePath, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('media').getPublicUrl(filePath);
      const { error: updateError } = await supabase.from('users').update({ avatar_url: publicUrl }).eq('id', currentUser!.id);
      if (updateError) throw updateError;
      setProfile(prev => prev ? { ...prev, avatar_url: publicUrl } : null);
      await refreshUser();
      toast.success('Avatar updated! 🎨');
    } catch (error) { console.error('Avatar upload error:', error); toast.error('Failed to update avatar'); }
    finally { setUploadingAvatar(false); }
  };

  const handleBannerChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be less than 5MB'); return; }
    try {
      setUploadingBanner(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${currentUser!.id}-banner-${Date.now()}.${fileExt}`;
      const filePath = `banners/${fileName}`;
      const { error: uploadError } = await supabase.storage.from('media').upload(filePath, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('media').getPublicUrl(filePath);
      const { error: updateError } = await supabase.from('users').update({ banner_url: publicUrl }).eq('id', currentUser!.id);
      if (updateError) throw updateError;
      setProfile(prev => prev ? { ...prev, banner_url: publicUrl } : null);
      await refreshUser();
      toast.success('Banner updated! 🎨');
    } catch (error) { console.error('Banner upload error:', error); toast.error('Failed to update banner'); }
    finally { setUploadingBanner(false); }
  };

  const toggleInterest    = (id: string) => setTempInterests(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const saveInterests     = () => { setEditedProfile({ ...editedProfile, interests: tempInterests }); setEditingInterests(false); toast.success('Interests updated!'); };
  const addFavoriteAnime  = () => {
    if (!newAnime.trim()) return;
    const current = editedProfile.favorite_anime || [];
    if (current.length >= 5) { toast.error('Maximum 5 favorite anime'); return; }
    setEditedProfile({ ...editedProfile, favorite_anime: [...current, newAnime.trim()] });
    setNewAnime('');
  };
  const removeFavoriteAnime = (i: number) => setEditedProfile({ ...editedProfile, favorite_anime: (editedProfile.favorite_anime || []).filter((_, idx) => idx !== i) });
  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const formatCount = (n: number) => n >= 1000000 ? `${(n/1000000).toFixed(1)}M` : n >= 1000 ? `${(n/1000).toFixed(1)}K` : String(n);
  const getRankInfo = (level: number) => LEVEL_RANKS.find(r => level >= r.min && level <= r.max) || LEVEL_RANKS[0];
  const getLevelProgress = (level: number) => {
    const r = getRankInfo(level);
    return Math.min(100, Math.max(0, ((level - r.min) / (r.max - r.min)) * 100));
  };

  // ── STYLE HELPERS ────────────────────────────────────────────
  const ink   = darkMode ? '#f0fafa'  : '#0d1a1a';
  const inkMid = darkMode ? 'rgba(240,250,250,0.6)' : 'rgba(13,26,26,0.55)';
  const inkFaint = darkMode ? 'rgba(90,184,184,0.25)' : 'rgba(13,26,26,0.1)';
  const cardBg = darkMode ? '#0f2424' : '#ffffff';
  const pageBg = darkMode ? '#0d1a1a' : '#e8f8f8';
  const inputBg = darkMode ? '#0d1a1a' : '#f0fafa';
  const inputBorder = darkMode ? 'rgba(90,184,184,0.2)' : 'rgba(13,26,26,0.15)';
  const borderColor = darkMode ? 'rgba(90,184,184,0.12)' : 'rgba(13,26,26,0.1)';

  const card = (extra?: React.CSSProperties): React.CSSProperties => ({
    background: cardBg,
    border: `1.5px solid ${borderColor}`,
    borderRadius: '14px',
    ...extra,
  });

  const goldBtn = (disabled?: boolean): React.CSSProperties => ({
    background: disabled ? 'rgba(232,168,32,0.4)' : '#e8a820',
    border: `2.5px solid ${disabled ? 'rgba(13,26,26,0.3)' : '#0d1a1a'}`,
    borderRadius: '8px',
    boxShadow: disabled ? 'none' : '3px 3px 0 #0d1a1a, 4px 4px 0 #8a5a00',
    color: '#0d1a1a',
    fontFamily: "'M PLUS Rounded 1c', sans-serif",
    fontWeight: 900,
    fontSize: '12px',
    letterSpacing: '1px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'transform 0.1s, box-shadow 0.1s',
    padding: '10px 20px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  });

  const ghostBtn = (active?: boolean): React.CSSProperties => ({
    background: active ? 'rgba(90,184,184,0.1)' : 'transparent',
    border: `1.5px solid ${active ? 'rgba(90,184,184,0.35)' : borderColor}`,
    borderRadius: '8px',
    color: active ? '#7ecece' : inkMid,
    fontFamily: "'M PLUS Rounded 1c', sans-serif",
    fontWeight: 900,
    fontSize: '11px',
    letterSpacing: '1px',
    cursor: 'pointer',
    padding: '8px 16px',
    transition: 'all 0.15s',
  });

  const inputStyle: React.CSSProperties = {
    background: inputBg,
    border: `1.5px solid ${inputBorder}`,
    borderRadius: '8px',
    color: ink,
    fontFamily: "'M PLUS Rounded 1c', sans-serif",
    fontSize: '13px',
    fontWeight: 700,
    padding: '10px 12px',
    width: '100%',
    outline: 'none',
  };

  // ── LOADING ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', border: `3px solid rgba(90,184,184,0.2)`, borderTop: `3px solid #7ecece`, animation: 'spin 0.8s linear infinite', margin: '0 auto 14px' }} />
          <p style={{ color: '#5ab8b8', fontSize: '11px', fontWeight: 900, letterSpacing: '2px' }}>LOADING PROFILE...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{ minHeight: '100vh', background: pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <FaUserCircle style={{ fontSize: 56, color: inkFaint, marginBottom: 14 }} />
          <p style={{ color: ink, fontWeight: 900, fontSize: 18, letterSpacing: '0.5px' }}>User not found</p>
          <p style={{ color: inkMid, fontSize: 13, marginTop: 6 }}>This profile doesn't exist</p>
        </div>
      </div>
    );
  }

  const mediaPosts    = userPosts.filter(p => p.media_url);
  const rankInfo      = getRankInfo(profile.level);
  const levelProgress = getLevelProgress(profile.level);

  return (
    <div style={{ minHeight: '100vh', background: pageBg, paddingBottom: 80 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=M+PLUS+Rounded+1c:wght@400;700;900&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }

        .onyx-profile-grid {
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 0 12px;
          max-width: 960px;
          margin: 0 auto;
        }
        @media (min-width: 768px) {
          .onyx-profile-grid {
            flex-direction: row;
            align-items: flex-start;
            padding: 0 20px;
            gap: 20px;
          }
        }

        .onyx-sidebar { width: 100%; }
        @media (min-width: 768px) { .onyx-sidebar { width: 280px; flex-shrink: 0; } }

        .onyx-posts-grid-3 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
        @media (min-width: 480px)  { .onyx-posts-grid-3 { grid-template-columns: repeat(3, 1fr); } }
        @media (min-width: 768px)  { .onyx-posts-grid-3 { gap: 10px; } }

        .onyx-tab-btn {
          padding: 8px 14px;
          border-radius: 6px;
          font-family: 'M PLUS Rounded 1c', sans-serif;
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 1px;
          cursor: pointer;
          transition: all 0.15s;
          border: none;
          white-space: nowrap;
        }

        .onyx-stat-btn {
          display: flex; flex-direction: column; align-items: center;
          gap: 2px; cursor: pointer; padding: 8px 0;
          transition: opacity 0.15s; background: none; border: none;
        }
        .onyx-stat-btn:hover { opacity: 0.75; }

        .onyx-input:focus { border-color: #5ab8b8 !important; }
        .onyx-gold-hover:hover:not(:disabled) { transform: translate(1px,1px) !important; box-shadow: 1px 1px 0 #0d1a1a !important; }

        /* Mobile: banner shorter */
        .onyx-banner { height: 160px; }
        @media (min-width: 768px) { .onyx-banner { height: 220px; } }

        /* Mobile: avatar overlaps banner less aggressively */
        .onyx-avatar-wrap { margin-top: -36px; }
        @media (min-width: 768px) { .onyx-avatar-wrap { margin-top: -44px; } }
      `}</style>

      {/* ── BANNER ───────────────────────────────────────────── */}
      <div className="onyx-banner" style={{ position: 'relative', overflow: 'hidden' }}>
        {profile.banner_url ? (
          <img src={profile.banner_url} alt="Banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{
            width: '100%', height: '100%',
            background: darkMode
              ? 'linear-gradient(135deg, #0d1a1a 0%, #1a2e2e 50%, #0f2424 100%)'
              : 'linear-gradient(135deg, #c8eded 0%, #a8d8d8 50%, #88c8c8 100%)',
          }}>
            {/* Subtle ink grid pattern */}
            <div style={{
              position: 'absolute', inset: 0,
              backgroundImage: `linear-gradient(rgba(90,184,184,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(90,184,184,0.08) 1px, transparent 1px)`,
              backgroundSize: '32px 32px',
            }} />
            {/* ONYX wordmark watermark */}
            <div style={{
              position: 'absolute', bottom: 16, left: 20,
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 'clamp(32px, 8vw, 56px)',
              letterSpacing: '8px',
              color: darkMode ? 'rgba(90,184,184,0.12)' : 'rgba(13,26,26,0.08)',
              userSelect: 'none',
              lineHeight: 1,
            }}>ONYX NETWORK</div>
          </div>
        )}

        {/* Banner overlay gradient */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 40%, rgba(13,26,26,0.6) 100%)' }} />

        {/* Edit banner — own profile only */}
        {isOwnProfile && (
          <label style={{ position: 'absolute', bottom: 10, right: 12, cursor: 'pointer', zIndex: 2 }}>
            <input type="file" accept="image/*" onChange={handleBannerChange} style={{ display: 'none' }} disabled={uploadingBanner} />
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(13,26,26,0.8)', backdropFilter: 'blur(8px)',
              border: '1.5px solid rgba(90,184,184,0.3)',
              borderRadius: '8px', padding: '6px 12px',
              color: '#7ecece', fontSize: '11px', fontWeight: 900, letterSpacing: '1px',
            }}>
              {uploadingBanner ? <FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} /> : <FaCamera />}
              <span>{uploadingBanner ? 'UPLOADING...' : 'EDIT BANNER'}</span>
            </div>
          </label>
        )}
      </div>

      {/* ── MAIN GRID ─────────────────────────────────────────── */}
      <div className="onyx-profile-grid" style={{ paddingTop: 0 }}>

        {/* ── LEFT SIDEBAR ──────────────────────────────────── */}
        <aside className="onyx-sidebar">

          {/* Avatar + identity card */}
          <div className="onyx-avatar-wrap" style={{ ...card(), padding: '0 20px 20px', position: 'relative' }}>

            {/* Avatar row */}
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', paddingTop: 8 }}>
              <div style={{ position: 'relative' }}>
                <img
                  src={profile.avatar_url || 'https://i.pravatar.cc/150'}
                  alt={profile.username}
                  style={{
                    width: 80, height: 80, borderRadius: '12px',
                    border: `3px solid ${cardBg}`,
                    objectFit: 'cover',
                    boxShadow: '0 4px 16px rgba(13,26,26,0.3)',
                  }}
                />
                {profile.ispremium && (
                  <div style={{
                    position: 'absolute', top: -6, right: -6,
                    background: '#e8a820', border: `2px solid ${cardBg}`,
                    borderRadius: '6px', padding: '3px 5px',
                  }}>
                    <FaCrown style={{ color: '#0d1a1a', fontSize: 10 }} />
                  </div>
                )}
                {isOwnProfile && (
                  <label style={{ position: 'absolute', bottom: -4, right: -4, cursor: 'pointer' }}>
                    <input type="file" accept="image/*" onChange={handleAvatarChange} style={{ display: 'none' }} disabled={uploadingAvatar} />
                    <div style={{
                      background: '#0d1a1a', border: `2px solid ${cardBg}`,
                      borderRadius: '6px', padding: '5px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {uploadingAvatar
                        ? <FaSpinner style={{ color: '#7ecece', fontSize: 10, animation: 'spin 0.8s linear infinite' }} />
                        : <FaCamera style={{ color: '#7ecece', fontSize: 10 }} />
                      }
                    </div>
                  </label>
                )}
              </div>

              {/* Edit / Follow button — top-right of card */}
              {isOwnProfile ? (
                <button
                  onClick={() => isEditing ? handleUpdateProfile() : setIsEditing(true)}
                  disabled={isUpdating}
                  style={goldBtn(isUpdating)}
                  className="onyx-gold-hover"
                >
                  {isUpdating ? <FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} /> : isEditing ? <FaCheck /> : <FaEdit />}
                  {isUpdating ? 'SAVING...' : isEditing ? 'SAVE' : 'EDIT PROFILE'}
                </button>
              ) : (
                <button
                  onClick={handleFollowToggle}
                  disabled={followLoading}
                  style={isFollowingUser ? ghostBtn(true) : goldBtn(followLoading)}
                  className="onyx-gold-hover"
                >
                  {followLoading
                    ? <FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} />
                    : isFollowingUser ? <FaUserCheck /> : <FaUserPlus />
                  }
                  {followLoading ? 'LOADING...' : isFollowingUser ? 'FOLLOWING' : 'FOLLOW'}
                </button>
              )}
            </div>

            {/* Name + rank */}
            <div style={{ marginTop: 14 }}>
              {isEditing ? (
                <input
                  value={editedProfile.username || ''}
                  onChange={e => setEditedProfile({ ...editedProfile, username: e.target.value })}
                  placeholder="Username"
                  className="onyx-input"
                  style={{ ...inputStyle, fontSize: '16px', fontWeight: 900, marginBottom: 8 }}
                />
              ) : (
                <h1 style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 'clamp(22px, 5vw, 28px)',
                  letterSpacing: '2px',
                  color: ink, margin: 0, lineHeight: 1,
                }}>
                  {profile.username}
                  {profile.ispremium && (
                    <span style={{
                      marginLeft: 8, fontSize: 10, fontFamily: "'M PLUS Rounded 1c', sans-serif",
                      background: '#e8a820', color: '#0d1a1a',
                      padding: '2px 7px', borderRadius: '4px', fontWeight: 900, letterSpacing: '1px',
                      verticalAlign: 'middle',
                    }}>PRO</span>
                  )}
                </h1>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 900, color: rankInfo.color, letterSpacing: '1px' }}>
                  {rankInfo.title.toUpperCase()}
                </span>
                <span style={{ color: inkFaint, fontSize: 10 }}>·</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#5ab8b8' }}>LVL {profile.level}</span>
              </div>
            </div>

            {/* XP bar */}
            <div style={{ marginTop: 12 }}>
              <div style={{
                width: '100%', height: '4px', borderRadius: '999px',
                background: darkMode ? 'rgba(90,184,184,0.12)' : 'rgba(13,26,26,0.08)',
                overflow: 'hidden',
              }}>
                <div style={{
                  width: `${levelProgress}%`, height: '100%',
                  background: '#e8a820', borderRadius: '999px',
                  transition: 'width 0.7s ease',
                }} />
              </div>
              <p style={{ fontSize: 10, fontWeight: 700, color: inkMid, letterSpacing: '0.5px', marginTop: 4 }}>
                {Math.round(levelProgress)}% TO NEXT RANK
              </p>
            </div>

            {/* Stats row — inline, tappable */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(4,1fr)',
              marginTop: 16, borderTop: `1px solid ${borderColor}`, paddingTop: 12, gap: 4,
            }}>
              {[
                { label: 'POSTS',     value: userPosts.length,          action: undefined },
                { label: 'MEDIA',     value: mediaPosts.length,         action: undefined },
                { label: 'FOLLOWERS', value: formatCount(profile.followers_count || 0), action: handleLoadFollowers, loading: loadingFollowers },
                { label: 'FOLLOWING', value: formatCount(profile.following_count || 0),  action: handleLoadFollowing, loading: loadingFollowing },
              ].map(s => (
                <button
                  key={s.label}
                  className="onyx-stat-btn"
                  onClick={s.action}
                  disabled={!s.action}
                  style={{ cursor: s.action ? 'pointer' : 'default' }}
                >
                  <span style={{ fontSize: 16, fontWeight: 900, color: ink, lineHeight: 1 }}>
                    {s.loading ? '...' : s.value}
                  </span>
                  <span style={{ fontSize: 9, fontWeight: 900, color: inkMid, letterSpacing: '0.5px' }}>{s.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Bio / Info card */}
          <div style={{ ...card({ marginTop: 10, padding: 18 }) }}>
            <p style={{ fontSize: 10, fontWeight: 900, color: inkMid, letterSpacing: '2px', marginBottom: 12 }}>ABOUT</p>

            {isEditing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <textarea
                  value={editedProfile.bio || ''}
                  onChange={e => setEditedProfile({ ...editedProfile, bio: e.target.value })}
                  placeholder="Bio..."
                  rows={3}
                  className="onyx-input"
                  style={{ ...inputStyle, resize: 'none' }}
                />
                <input
                  value={editedProfile.location || ''}
                  onChange={e => setEditedProfile({ ...editedProfile, location: e.target.value })}
                  placeholder="📍 Location"
                  className="onyx-input"
                  style={inputStyle}
                />
                <input
                  value={editedProfile.website || ''}
                  onChange={e => setEditedProfile({ ...editedProfile, website: e.target.value })}
                  placeholder="🔗 Website"
                  className="onyx-input"
                  style={inputStyle}
                />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {profile.bio && (
                  <p style={{ fontSize: 13, fontWeight: 700, color: ink, lineHeight: 1.6, margin: 0 }}>
                    {profile.bio}
                  </p>
                )}
                {[
                  profile.location && { icon: <FaMapMarkerAlt style={{ color: '#5ab8b8', fontSize: 11 }} />, text: profile.location, href: undefined },
                  profile.website  && { icon: <FaLink style={{ color: '#5ab8b8', fontSize: 11 }} />, text: profile.website.replace(/^https?:\/\//, ''), href: profile.website },
                  { icon: <FaCalendar style={{ color: '#5ab8b8', fontSize: 11 }} />, text: `Joined ${formatDate(profile.created_at)}`, href: undefined },
                ].filter(Boolean).map((item: any, i) => (
                  item.href ? (
                    <a key={i} href={item.href} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, color: '#5ab8b8', textDecoration: 'none' }}>
                      {item.icon}<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.text}</span>
                    </a>
                  ) : (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, color: inkMid }}>
                      {item.icon}<span>{item.text}</span>
                    </div>
                  )
                ))}
                {!profile.bio && !profile.location && !profile.website && (
                  <p style={{ fontSize: 12, color: inkMid }}>
                    {isOwnProfile ? 'Add a bio to introduce yourself.' : 'No info added yet.'}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Interests card */}
          <div style={{ ...card({ marginTop: 10, padding: 18 }) }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <p style={{ fontSize: 10, fontWeight: 900, color: inkMid, letterSpacing: '2px', margin: 0 }}>INTERESTS</p>
              {isOwnProfile && (
                <button
                  onClick={() => editingInterests ? saveInterests() : setEditingInterests(true)}
                  style={{ ...ghostBtn(editingInterests), padding: '4px 10px', fontSize: 10 }}
                >
                  {editingInterests ? 'DONE' : 'EDIT'}
                </button>
              )}
            </div>

            {editingInterests ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {AVAILABLE_INTERESTS.map(interest => {
                  const Icon = interest.icon;
                  const selected = tempInterests.includes(interest.id);
                  return (
                    <button
                      key={interest.id}
                      onClick={() => toggleInterest(interest.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '8px 10px', borderRadius: '8px',
                        background: selected ? 'rgba(232,168,32,0.1)' : (darkMode ? '#0d1a1a' : '#f0fafa'),
                        border: `1.5px solid ${selected ? '#e8a820' : borderColor}`,
                        color: selected ? '#e8a820' : inkMid,
                        fontSize: 11, fontWeight: 900, cursor: 'pointer',
                        letterSpacing: '0.5px',
                      }}
                    >
                      <Icon style={{ fontSize: 11, flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{interest.label}</span>
                      {selected && <FaCheck style={{ marginLeft: 'auto', fontSize: 9, flexShrink: 0 }} />}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(profile.interests || []).length > 0
                  ? (profile.interests || []).map(id => {
                      const interest = AVAILABLE_INTERESTS.find(i => i.id === id);
                      if (!interest) return null;
                      const Icon = interest.icon;
                      return (
                        <span key={id} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '5px 10px', borderRadius: '6px',
                          background: darkMode ? '#0d1a1a' : '#f0fafa',
                          border: `1.5px solid ${borderColor}`,
                          color: interest.color,
                          fontSize: 11, fontWeight: 900, letterSpacing: '0.5px',
                        }}>
                          <Icon style={{ fontSize: 10 }} />
                          <span>{interest.label}</span>
                        </span>
                      );
                    })
                  : <p style={{ fontSize: 12, color: inkMid }}>{isOwnProfile ? 'Add your interests.' : 'None added yet.'}</p>
                }
              </div>
            )}
          </div>

          {/* Favourite anime card */}
          <div style={{ ...card({ marginTop: 10, padding: 18 }) }}>
            <p style={{ fontSize: 10, fontWeight: 900, color: inkMid, letterSpacing: '2px', marginBottom: 12 }}>FAVOURITE ANIME</p>

            {isEditing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(editedProfile.favorite_anime || []).map((anime, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: ink,
                      background: darkMode ? '#0d1a1a' : '#f0fafa',
                      border: `1.5px solid ${borderColor}`, borderRadius: '6px', padding: '6px 10px' }}>
                      {anime}
                    </span>
                    <button onClick={() => removeFavoriteAnime(i)}
                      style={{ background: 'none', border: 'none', color: '#e05050', cursor: 'pointer', padding: 4 }}>
                      <FaTimes style={{ fontSize: 12 }} />
                    </button>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    value={newAnime}
                    onChange={e => setNewAnime(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && addFavoriteAnime()}
                    placeholder="Add anime..."
                    className="onyx-input"
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button onClick={addFavoriteAnime} style={{
                    background: '#e8a820', border: '2px solid #0d1a1a',
                    borderRadius: '8px', color: '#0d1a1a', padding: '8px 12px',
                    cursor: 'pointer', flexShrink: 0,
                  }}>
                    <FaPlus style={{ fontSize: 12 }} />
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(profile.favorite_anime || []).length > 0
                  ? (profile.favorite_anime || []).map((anime, i) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 12px', borderRadius: '8px',
                        background: darkMode ? '#0d1a1a' : '#f0fafa',
                        border: `1.5px solid ${borderColor}`,
                      }}>
                        <span style={{ fontSize: 10, fontWeight: 900, color: '#e8a820', minWidth: 16 }}>{i + 1}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: ink }}>{anime}</span>
                      </div>
                    ))
                  : <p style={{ fontSize: 12, color: inkMid }}>{isOwnProfile ? 'Add your favourites.' : 'None added yet.'}</p>
                }
              </div>
            )}
          </div>

          {/* Cancel edit — only when editing */}
          {isEditing && (
            <button
              onClick={() => { setIsEditing(false); setEditedProfile(profile); }}
              style={{ ...ghostBtn(), width: '100%', marginTop: 10, padding: '10px', justifyContent: 'center' }}
            >
              CANCEL EDIT
            </button>
          )}
        </aside>

        {/* ── RIGHT — POSTS ─────────────────────────────────── */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Tab bar */}
          <div style={{ ...card({ padding: '10px 12px', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }) }}>
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto' }}>
              {(['posts', 'media', ...(isOwnProfile ? ['saved'] : [])] as const).map(tab => (
                <button
                  key={tab}
                  className="onyx-tab-btn"
                  onClick={() => {
                    setActiveTab(tab as any);
                    if (tab === 'saved' && savedPosts.length === 0 && !loadingSavedPosts) loadSavedPosts();
                  }}
                  style={{
                    background: activeTab === tab ? '#e8a820' : 'transparent',
                    border: `1.5px solid ${activeTab === tab ? '#0d1a1a' : borderColor}`,
                    color: activeTab === tab ? '#0d1a1a' : inkMid,
                    boxShadow: activeTab === tab ? '2px 2px 0 #0d1a1a' : 'none',
                  }}
                >
                  {tab.toUpperCase()}
                </button>
              ))}
            </div>

            {/* View mode toggle */}
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              {[{ mode: 'grid', Icon: FaThLarge }, { mode: 'list', Icon: FaList }].map(({ mode, Icon }) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode as any)}
                  style={{
                    ...ghostBtn(viewMode === mode),
                    padding: '7px 9px',
                    display: 'flex', alignItems: 'center',
                  }}
                >
                  <Icon style={{ fontSize: 12 }} />
                </button>
              ))}
            </div>
          </div>

          {/* ── POSTS TAB ── */}
          {activeTab === 'posts' && (
            viewMode === 'grid' ? (
              <div className="onyx-posts-grid-3">
                {userPosts.map(post => (
                  <GridCell key={post.id} post={post} darkMode={darkMode} formatCount={formatCount} />
                ))}
                {userPosts.length === 0 && <EmptyState darkMode={darkMode} label="No posts yet" isOwn={isOwnProfile} />}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {userPosts.map(post => (
                  <ListCard key={post.id} post={post} darkMode={darkMode} card={card} ink={ink} inkMid={inkMid} borderColor={borderColor} formatCount={formatCount} />
                ))}
                {userPosts.length === 0 && <EmptyState darkMode={darkMode} label="No posts yet" isOwn={isOwnProfile} />}
              </div>
            )
          )}

          {/* ── MEDIA TAB ── */}
          {activeTab === 'media' && (
            <div className="onyx-posts-grid-3">
              {mediaPosts.map(post => (
                <GridCell key={post.id} post={post} darkMode={darkMode} formatCount={formatCount} />
              ))}
              {mediaPosts.length === 0 && <EmptyState darkMode={darkMode} label="No media yet" isOwn={isOwnProfile} />}
            </div>
          )}

          {/* ── SAVED TAB ── */}
          {activeTab === 'saved' && (
            loadingSavedPosts ? (
              <div style={{ textAlign: 'center', padding: '48px 0' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', border: `3px solid rgba(90,184,184,0.2)`, borderTop: `3px solid #7ecece`, animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                <p style={{ color: '#5ab8b8', fontSize: 11, fontWeight: 900, letterSpacing: '2px' }}>LOADING...</p>
              </div>
            ) : savedPosts.length === 0 ? (
              <EmptyState darkMode={darkMode} label="No saved posts yet" isOwn={isOwnProfile} />
            ) : (
              <div className="onyx-posts-grid-3">
                {savedPosts.map(sp => (
                  <SavedCell
                    key={sp.id}
                    savedPost={sp}
                    darkMode={darkMode}
                    removingId={removingId}
                    onUnsave={handleUnsave}
                    formatCount={formatCount}
                    borderColor={borderColor}
                    cardBg={cardBg}
                    ink={ink}
                    inkMid={inkMid}
                  />
                ))}
              </div>
            )
          )}
        </div>
      </div>

      {/* ── FOLLOWERS MODAL ───────────────────────────────────── */}
      {showFollowersModal && (
        <FollowModal
          title="FOLLOWERS"
          users={followersData}
          darkMode={darkMode}
          onClose={() => setShowFollowersModal(false)}
          card={card}
          ink={ink}
          inkMid={inkMid}
          borderColor={borderColor}
          formatCount={formatCount}
        />
      )}

      {/* ── FOLLOWING MODAL ───────────────────────────────────── */}
      {showFollowingModal && (
        <FollowModal
          title="FOLLOWING"
          users={followingData}
          darkMode={darkMode}
          onClose={() => setShowFollowingModal(false)}
          card={card}
          ink={ink}
          inkMid={inkMid}
          borderColor={borderColor}
          formatCount={formatCount}
        />
      )}
    </div>
  );
};

// ── SUB-COMPONENTS (pure display, no logic) ──────────────────────

function GridCell({ post, darkMode, formatCount }: {
  post: Post; darkMode: boolean; formatCount: (n: number) => string;
}) {
  const borderColor = darkMode ? 'rgba(90,184,184,0.12)' : 'rgba(13,26,26,0.1)';
  return (
    <div style={{
      aspectRatio: '1',
      borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', position: 'relative',
      background: darkMode ? '#0f2424' : '#e8f8f8',
      border: `1.5px solid ${borderColor}`,
    }}
      className="onyx-grid-cell"
    >
      {post.media_url ? (
        <>
          <img src={post.media_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          {post.media_type === 'video' && (
            <div style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(13,26,26,0.8)', borderRadius: '4px', padding: '3px 5px' }}>
              <FaVideo style={{ color: '#7ecece', fontSize: 9 }} />
            </div>
          )}
        </>
      ) : (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: darkMode ? 'rgba(240,250,250,0.6)' : 'rgba(13,26,26,0.55)', textAlign: 'center', lineHeight: 1.4,
            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical' }}>
            {post.content}
          </p>
        </div>
      )}
      {/* Hover overlay */}
      <div className="onyx-grid-hover" style={{
        position: 'absolute', inset: 0,
        background: 'rgba(13,26,26,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
        opacity: 0, transition: 'opacity 0.2s',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#f0fafa', fontSize: 12, fontWeight: 900 }}>
          <FaHeart style={{ color: '#e8a820' }} />{formatCount(post.likes)}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#f0fafa', fontSize: 12, fontWeight: 900 }}>
          <FaComment style={{ color: '#7ecece' }} />{formatCount(post.comments_count)}
        </span>
      </div>
      <style>{`.onyx-grid-cell:hover .onyx-grid-hover { opacity: 1 !important; }`}</style>
    </div>
  );
}

function ListCard({ post, darkMode, card, ink, inkMid, borderColor, formatCount }: {
  post: Post; darkMode: boolean;
  card: (e?: React.CSSProperties) => React.CSSProperties;
  ink: string; inkMid: string; borderColor: string;
  formatCount: (n: number) => string;
}) {
  return (
    <div style={{ ...card({ padding: 16 }) }}>
      <p style={{ fontSize: 13, fontWeight: 700, color: ink, lineHeight: 1.6, marginBottom: post.media_url ? 12 : 0 }}>
        {post.content}
      </p>
      {post.media_url && (
        <div style={{ borderRadius: '8px', overflow: 'hidden', border: `1.5px solid ${borderColor}`, position: 'relative' }}>
          <img src={post.media_url} alt="" style={{ width: '100%', display: 'block', maxHeight: 280, objectFit: 'cover' }} />
          {post.media_type === 'video' && (
            <div style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(13,26,26,0.8)', borderRadius: '6px', padding: '4px 8px' }}>
              <FaVideo style={{ color: '#7ecece', fontSize: 12 }} />
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
        {[
          { icon: <FaHeart style={{ color: '#e8a820', fontSize: 13 }} />, count: post.likes },
          { icon: <FaComment style={{ color: '#5ab8b8', fontSize: 13 }} />, count: post.comments_count },
        ].map((a, i) => (
          <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 900, color: inkMid }}>
            {a.icon}{formatCount(a.count)}
          </span>
        ))}
      </div>
    </div>
  );
}

function SavedCell({ savedPost, darkMode, removingId, onUnsave, formatCount, borderColor, cardBg, ink, inkMid }: {
  savedPost: SavedPost; darkMode: boolean;
  removingId: string | null; onUnsave: (id: string) => void;
  formatCount: (n: number) => string; borderColor: string;
  cardBg: string; ink: string; inkMid: string;
}) {
  return (
    <div style={{
      aspectRatio: '1', borderRadius: '10px', overflow: 'hidden',
      cursor: 'pointer', position: 'relative',
      background: darkMode ? '#0f2424' : '#e8f8f8',
      border: `1.5px solid ${borderColor}`,
    }} className="onyx-grid-cell">
      {savedPost.post.media_url ? (
        <img src={savedPost.post.media_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      ) : (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: inkMid, textAlign: 'center', lineHeight: 1.4 }}>
            {savedPost.post.content}
          </p>
        </div>
      )}
      {/* Hover overlay */}
      <div className="onyx-grid-hover" style={{
        position: 'absolute', inset: 0, background: 'rgba(13,26,26,0.75)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12,
        opacity: 0, transition: 'opacity 0.2s',
      }}>
        <div style={{ display: 'flex', gap: 14 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#f0fafa', fontSize: 12, fontWeight: 900 }}>
            <FaHeart style={{ color: '#e8a820' }} />{formatCount(savedPost.post.likes)}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#f0fafa', fontSize: 12, fontWeight: 900 }}>
            <FaComment style={{ color: '#7ecece' }} />{formatCount(savedPost.post.comments_count)}
          </span>
        </div>
        <button
          onClick={e => { e.stopPropagation(); onUnsave(savedPost.post_id); }}
          disabled={removingId === savedPost.post_id}
          style={{
            background: 'rgba(224,80,80,0.15)', border: '1.5px solid rgba(224,80,80,0.4)',
            color: '#e05050', borderRadius: '6px', padding: '5px 10px',
            fontSize: 10, fontWeight: 900, cursor: 'pointer', letterSpacing: '1px',
            display: 'flex', alignItems: 'center', gap: 5,
          }}
        >
          {removingId === savedPost.post_id
            ? <FaSpinner style={{ animation: 'spin 0.8s linear infinite', fontSize: 10 }} />
            : <FaTrash style={{ fontSize: 10 }} />
          }
          {removingId === savedPost.post_id ? 'REMOVING...' : 'REMOVE'}
        </button>
      </div>
    </div>
  );
}

function EmptyState({ darkMode, label, isOwn }: { darkMode: boolean; label: string; isOwn: boolean }) {
  const ink = darkMode ? '#f0fafa' : '#0d1a1a';
  const inkMid = darkMode ? 'rgba(240,250,250,0.5)' : 'rgba(13,26,26,0.5)';
  const borderColor = darkMode ? 'rgba(90,184,184,0.12)' : 'rgba(13,26,26,0.1)';
  return (
    <div style={{
      gridColumn: '1/-1',
      padding: '48px 24px', textAlign: 'center',
      background: darkMode ? '#0f2424' : '#ffffff',
      border: `1.5px solid ${borderColor}`,
      borderRadius: '14px',
    }}>
      <FaImage style={{ fontSize: 40, color: darkMode ? 'rgba(90,184,184,0.15)' : 'rgba(13,26,26,0.1)', marginBottom: 12 }} />
      <p style={{ fontSize: 14, fontWeight: 900, color: ink, letterSpacing: '0.5px', marginBottom: 6 }}>{label}</p>
      {isOwn && <p style={{ fontSize: 12, color: inkMid }}>Share your first post from the feed.</p>}
    </div>
  );
}

function FollowModal({ title, users, darkMode, onClose, card, ink, inkMid, borderColor, formatCount }: {
  title: string; users: FollowUser[]; darkMode: boolean; onClose: () => void;
  card: (e?: React.CSSProperties) => React.CSSProperties;
  ink: string; inkMid: string; borderColor: string; formatCount: (n: number) => string;
}) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50,
      background: 'rgba(13,26,26,0.75)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        ...card(),
        width: '100%', maxWidth: 400, maxHeight: '80vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '6px 6px 0 rgba(13,26,26,0.3)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderBottom: `1px solid ${borderColor}` }}>
          <p style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, letterSpacing: '3px', color: ink, margin: 0 }}>{title}</p>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: inkMid, cursor: 'pointer', padding: 4 }}>
            <FaTimes style={{ fontSize: 16 }} />
          </button>
        </div>

        {/* List */}
        <div style={{ overflowY: 'auto', padding: '12px 16px', flex: 1 }}>
          {users.length > 0 ? users.map(u => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px solid ${borderColor}` }}>
              <img src={u.avatar_url || 'https://i.pravatar.cc/150'} alt={u.username}
                style={{ width: 42, height: 42, borderRadius: '8px', objectFit: 'cover', border: `1.5px solid ${borderColor}` }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <p style={{ fontSize: 13, fontWeight: 900, color: ink, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.username}</p>
                  {u.ispremium && <FaCrown style={{ color: '#e8a820', fontSize: 10, flexShrink: 0 }} />}
                </div>
                {u.bio && <p style={{ fontSize: 11, color: inkMid, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.bio}</p>}
              </div>
              <span style={{
                fontSize: 10, fontWeight: 900, letterSpacing: '0.5px',
                color: '#5ab8b8', background: 'rgba(90,184,184,0.1)',
                border: '1px solid rgba(90,184,184,0.2)',
                borderRadius: '4px', padding: '3px 7px', flexShrink: 0,
              }}>LVL {u.level}</span>
            </div>
          )) : (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <FaUsers style={{ fontSize: 36, color: darkMode ? 'rgba(90,184,184,0.15)' : 'rgba(13,26,26,0.1)', marginBottom: 10 }} />
              <p style={{ fontSize: 12, color: inkMid }}>Nobody here yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;